import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, AlertTriangle, Lock, Home, RefreshCw } from 'lucide-react';
import { useI18n } from '../../i18n';
import fr from '../../i18n/locales/fr';

const ErrorPage = () => {
  const { t: originalT } = useI18n();

  // Use French translations for the error page (French platform consistency)
  const t = (key, params = {}) => {
    const keys = key.split('.');
    let value = fr;
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return key;
      }
    }
    if (typeof value !== 'string') return key;
    return value.replace(/\{(\w+)\}/g, (match, param) => {
      return params[param] !== undefined ? params[param] : match;
    });
  };
  const navigate = useNavigate();
  const location = useLocation();
  const [countdown, setCountdown] = useState(5);

  // Get error code from path or state
  const errorCode = location.state?.code ||
    (location.pathname.includes('403') ? 403 :
     location.pathname.includes('401') ? 401 :
     location.pathname.includes('500') ? 500 :
     location.pathname.includes('503') ? 503 : 404);

  const errorMessage = location.state?.message;

  // Error configurations
  const errorConfigs = {
    401: {
      title: t('errors.401.title'),
      subtitle: t('errors.401.subtitle'),
      description: t('errors.401.description'),
      icon: Lock,
      color: "text-yellow-500",
      bgColor: "bg-yellow-500/10",
      borderColor: "border-yellow-500/20",
      redirectPath: "/login",
      redirectText: t('errors.401.redirectText')
    },
    403: {
      title: t('errors.403.title'),
      subtitle: t('errors.403.subtitle'),
      description: t('errors.403.description'),
      icon: Shield,
      color: "text-red-500",
      bgColor: "bg-red-500/10",
      borderColor: "border-red-500/20",
      redirectPath: "/dashboard",
      redirectText: t('errors.403.redirectText')
    },
    404: {
      title: t('errors.404.title'),
      subtitle: t('errors.404.subtitle'),
      description: t('errors.404.description'),
      icon: AlertTriangle,
      color: "text-brand-green",
      bgColor: "bg-brand-green/10",
      borderColor: "border-brand-green/20",
      redirectPath: "/dashboard",
      redirectText: t('errors.404.redirectText')
    },
    500: {
      title: t('errors.500.title'),
      subtitle: t('errors.500.subtitle'),
      description: t('errors.500.description'),
      icon: AlertTriangle,
      color: "text-orange-500",
      bgColor: "bg-orange-500/10",
      borderColor: "border-orange-500/20",
      redirectPath: "/dashboard",
      redirectText: t('errors.500.redirectText')
    },
    503: {
      title: t('errors.503.title'),
      subtitle: t('errors.503.subtitle'),
      description: t('errors.503.description'),
      icon: AlertTriangle,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
      borderColor: "border-blue-500/20",
      redirectPath: "/dashboard",
      redirectText: t('errors.503.redirectText')
    }
  };

  const config = errorConfigs[errorCode] || errorConfigs[404];
  const IconComponent = config.icon;

  // Countdown and redirect
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate(config.redirectPath);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, config.redirectPath]);

  // Matrix background
  const matrixColumns = useMemo(() => {
    const chars = '01';
    return Array.from({ length: 20 }, (_, i) => ({
      id: i,
      left: `${(i / 20) * 100}%`,
      delay: Math.random() * 5,
      duration: 8 + Math.random() * 12,
      chars: Array.from({ length: 30 }, () => chars[Math.floor(Math.random() * chars.length)]).join('')
    }));
  }, []);

  const handleGoHome = () => {
    navigate('/dashboard');
  };

  const handleRetry = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center relative overflow-hidden px-4">
      {/* Animated Background */}
      <div className="absolute inset-0">
        {/* Grid Pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 255, 136, 0.5) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(0, 255, 136, 0.5) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />

        {/* Matrix Rain */}
        <div className="absolute inset-0 overflow-hidden">
          {matrixColumns.map((col) => (
            <div
              key={col.id}
              className="absolute top-0 text-brand-green/10 font-mono text-xs whitespace-pre leading-4"
              style={{
                left: col.left,
                animation: `matrix-fall ${col.duration}s linear infinite`,
                animationDelay: `${col.delay}s`
              }}
            >
              {col.chars.split('').map((char, i) => (
                <div key={i} style={{ opacity: Math.random() * 0.3 + 0.1 }}>{char}</div>
              ))}
            </div>
          ))}
        </div>

        {/* Radial Glow */}
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] ${config.bgColor} rounded-full blur-[150px] opacity-30`} />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-lg w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/logo.png"
            alt="SYLINK"
            className="h-12 w-auto mx-auto mb-6 opacity-80"
          />
        </div>

        {/* Error Card */}
        <div className={`${config.bgColor} border ${config.borderColor} rounded-2xl p-8 backdrop-blur-sm`}>
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className={`w-20 h-20 rounded-full ${config.bgColor} border ${config.borderColor} flex items-center justify-center`}>
              <IconComponent className={`w-10 h-10 ${config.color}`} />
            </div>
          </div>

          {/* Error Code */}
          <div className="text-center mb-4">
            <span className={`text-7xl font-bold ${config.color} opacity-80`}>{errorCode}</span>
          </div>

          {/* Title */}
          <h1 className={`text-2xl font-bold text-center ${config.color} mb-2`}>
            {config.subtitle}
          </h1>

          {/* Description */}
          <p className="text-gray-400 text-center mb-6">
            {errorMessage || config.description}
          </p>

          {/* Countdown */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#111111] border border-gray-800">
              <RefreshCw className={`w-4 h-4 ${config.color} animate-spin`} />
              <span className="text-gray-400 text-sm">
                {config.redirectText} {t('errors.redirectingIn')} <span className={`font-bold ${config.color}`}>{countdown}</span> {t('errors.seconds')}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleGoHome}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-brand-green hover:bg-brand-green/90 text-black font-semibold rounded-xl transition-all duration-200 shadow-lg shadow-brand-green/20"
            >
              <Home className="w-5 h-5" />
              <span>{t('errors.backToDashboard')}</span>
            </button>

            {(errorCode === 500 || errorCode === 503) && (
              <button
                onClick={handleRetry}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-[#111111] hover:bg-[#161616] text-gray-300 hover:text-white font-semibold rounded-xl border border-gray-800 hover:border-gray-700 transition-all duration-200"
              >
                <RefreshCw className="w-5 h-5" />
                <span>{t('errors.retry')}</span>
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-600">
            {t('errors.supportContact')}
          </p>
        </div>
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes matrix-fall {
          0% {
            transform: translateY(-100%);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(100vh);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default ErrorPage;
